import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Children, cloneElement, useId, useMemo, useState } from 'react';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useTheme } from '../../theme';
import { useIsTouchDevice } from '../../hooks';
import { useTooltipStyles } from './tooltip.styles';
import { TooltipPlain } from './plain';
import { useTooltip } from './useTooltip';
import { TooltipArrow } from './tooltipArrow';
import { chainHandlers, getElementRef, mergeRefs } from './tooltip.utils';
/** Arrow length (px) from the tooltip body to its tip. */ const ARROW_SIZE = 7;
/**
 * Baseline distance (px) from the anchor to the tooltip body, before the caller's
 * `offset` is added. Matches the gap the previous `@mui/material/Tooltip` used
 * (its `marginBottom: 14px`), so existing per-call `offset` values land identically.
 */ const ARROW_GAP = 14;
const DEFAULT_FALLBACK_PLACEMENTS = [
    'top',
    'bottom',
    'right',
    'left'
];
/**
 * Tooltip built on Popper + ClickAwayListener (the same primitives `popover` and
 * `infoIcon` use). It owns its open/close state via {@link useTooltip}, which makes it
 * close reliably on touch — unlike the previous `@mui/material/Tooltip` wrapper, which
 * stayed open after a tap because emulated mouse events kept re-opening it.
 *
 * The public prop surface mirrors the props the ~30 consumers actually used, including
 * the controlled (`open`/`onOpen`/`onClose`), `componentsProps`, and `disable*Listener`
 * paths relied on by `infoIcon`, `menuCell`, and `callControlBarCell`.
 */ export const Tooltip = ({ children, title, placement = 'top', offset, popoverFallbackPlacements = DEFAULT_FALLBACK_PLACEMENTS, isDisabled = false, isFaded = false, open, onOpen, onClose, disableHoverListener = false, disableFocusListener = false, disableTouchListener = false, disableInteractive = false, arrow = true, enterDelay = 200, leaveDelay = 50, id, className, onTouchStart, onTouchEnd, componentsProps })=>{
    const theme = useTheme('dark');
    const styles = useTooltipStyles('dark');
    const isTouchDevice = useIsTouchDevice();
    const generatedId = useId();
    const [arrowEl, setArrowEl] = useState(null);
    // Stable element reference so the popup body subtree (TooltipPlain → Caption →
    // Box) isn't re-rendered on every Grow transition phase / popper position update.
    const content = useMemo(()=>typeof title === 'string' && Boolean(title) ? /*#__PURE__*/ _jsx(TooltipPlain, {
            text: title
        }) : title, [
        title
    ]);
    const hasContent = !isDisabled && Boolean(content);
    const tooltip = useTooltip({
        open,
        onOpen,
        onClose,
        enterDelay,
        leaveDelay,
        disableHoverListener,
        disableFocusListener,
        disableTouchListener,
        disableInteractive,
        isTouchDevice
    });
    const child = Children.only(children);
    const childProps = child.props;
    const childRef = getElementRef(child);
    const { setAnchorRef } = tooltip;
    const mergedRef = useMemo(()=>mergeRefs(childRef, setAnchorRef), [
        childRef,
        setAnchorRef
    ]);
    const modifiers = useMemo(()=>{
        const base = typeof offset === 'string' ? Number.parseInt(offset, 10) : offset ?? 0;
        const distance = base + (arrow ? ARROW_GAP : 0);
        return [
            {
                name: 'offset',
                options: {
                    offset: [
                        0,
                        distance
                    ]
                }
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: popoverFallbackPlacements
                }
            },
            {
                name: 'preventOverflow',
                options: {
                    altAxis: true,
                    tether: false,
                    boundary: 'clippingParents'
                }
            },
            {
                name: 'arrow',
                enabled: arrow,
                options: {
                    element: arrowEl,
                    padding: 4
                }
            }
        ];
    }, [
        offset,
        arrow,
        popoverFallbackPlacements,
        arrowEl
    ]);
    // Stable element reference (setArrowEl is a stable state setter), so the arrow
    // isn't re-rendered on every Grow transition phase.
    const arrowElement = useMemo(()=>arrow ? /*#__PURE__*/ _jsx(TooltipArrow, {
            ref: setArrowEl,
            size: ARROW_SIZE,
            color: theme.tooltip.background
        }) : null, [
        arrow,
        theme.tooltip.background
    ]);
    const popperSx = useMemo(()=>[
            {
                zIndex: theme.zIndex.tooltip
            },
            ...isFaded ? [
                styles.faded
            ] : [],
            ...componentsProps?.popper?.sx ? Array.isArray(componentsProps.popper.sx) ? componentsProps.popper.sx : [
                componentsProps.popper.sx
            ] : []
        ], [
        theme.zIndex.tooltip,
        isFaded,
        styles.faded,
        componentsProps?.popper?.sx
    ]);
    // No content to show — render the anchor untouched, but still forward the
    // touch handlers (matches the old behaviour of nulling the title).
    if (!hasContent) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ cloneElement(child, {
                onTouchStart: chainHandlers(childProps.onTouchStart, onTouchStart),
                onTouchEnd: chainHandlers(childProps.onTouchEnd, onTouchEnd)
            })
        });
    }
    const effectiveTooltipId = componentsProps?.tooltip?.id ?? id ?? generatedId;
    // When a consumer sets its own tooltip id it manages aria itself (e.g. infoIcon).
    const consumerManagesAria = componentsProps?.tooltip?.id !== undefined;
    const anchorAriaDescribedBy = !consumerManagesAria && tooltip.isOpen ? [
        childProps['aria-describedby'],
        effectiveTooltipId
    ].filter(Boolean).join(' ') : childProps['aria-describedby'];
    const anchor = /*#__PURE__*/ cloneElement(child, {
        ref: mergedRef,
        'aria-describedby': anchorAriaDescribedBy,
        onMouseEnter: chainHandlers(childProps.onMouseEnter, tooltip.anchorListeners.onMouseEnter),
        onMouseLeave: chainHandlers(childProps.onMouseLeave, tooltip.anchorListeners.onMouseLeave),
        onFocus: chainHandlers(childProps.onFocus, tooltip.anchorListeners.onFocus),
        onBlur: chainHandlers(childProps.onBlur, tooltip.anchorListeners.onBlur),
        // Touch tap-toggle (the anchorListener) is merged with the child's own onClick.
        onClick: chainHandlers(childProps.onClick, tooltip.anchorListeners.onClick),
        // Tooltip-level touch handlers are forwarded to the anchor (e.g. callControlBarCell).
        onTouchStart: chainHandlers(childProps.onTouchStart, onTouchStart),
        onTouchEnd: chainHandlers(childProps.onTouchEnd, onTouchEnd)
    });
    const { ref: popupRef, role: popupRole, onMouseEnter: popupOnMouseEnter, onMouseLeave: popupOnMouseLeave, ...popupRest } = componentsProps?.tooltip ?? {};
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            anchor,
            tooltip.isOpen && tooltip.anchorEl && /*#__PURE__*/ _jsx(ClickAwayListener, {
                onClickAway: tooltip.handleClickAway,
                children: /*#__PURE__*/ _jsx(Popper, {
                    open: true,
                    anchorEl: tooltip.anchorEl,
                    placement: placement,
                    modifiers: modifiers,
                    // Neutralise MUI Popper's default `role="tooltip"` on the positioning
                    // root; the role belongs on the body element below (which also carries
                    // the id that `aria-describedby` targets), not the wrapper.
                    role: "presentation",
                    className: className,
                    sx: popperSx,
                    children: /*#__PURE__*/ _jsxs("div", {
                        css: {
                            position: 'relative'
                        },
                        children: [
                            arrowElement,
                            /*#__PURE__*/ _jsx("div", {
                                ...popupRest,
                                ref: popupRef,
                                id: effectiveTooltipId,
                                role: popupRole ?? 'tooltip',
                                onMouseEnter: chainHandlers(tooltip.popperListeners.onMouseEnter, popupOnMouseEnter),
                                onMouseLeave: chainHandlers(tooltip.popperListeners.onMouseLeave, popupOnMouseLeave),
                                css: styles.tooltip,
                                children: content
                            })
                        ]
                    })
                })
            })
        ]
    });
};

//# sourceMappingURL=tooltip.js.map